/**
 * FamilyAcceptPage.jsx
 *
 * Handles the family-member invite-accept flow.
 * Route: /family/accept?token=<invite_token>
 *
 * On mount: validates the token with GET /api/family/accept?token=...
 *   → If valid:  shows a set-name + set-password form
 *   → If invalid: shows an error card
 *
 * On submit: POST /api/family/accept → sets session cookie → redirects to /
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Crown, Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function FamilyAcceptPage() {
  const [searchParams]        = useSearchParams();
  const navigate              = useNavigate();
  const token                 = searchParams.get('token') || '';

  const [status, setStatus]   = useState('loading'); // loading | valid | invalid | submitting | done | error
  const [invite, setInvite]   = useState({ email: '', name: '' });
  const [name, setName]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw]   = useState(false);
  const [errMsg, setErrMsg]   = useState('');

  // ── Validate token on mount ──────────────────────────────────────────────
  useEffect(() => {
    if (!token) { setStatus('invalid'); setErrMsg('No invite token provided.'); return; }
    fetch(`${API}/family/accept?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.detail || 'Invalid invite.');
        return data;
      })
      .then((data) => {
        setInvite({ email: data.email, name: data.name || '' });
        setName(data.name || '');
        setStatus('valid');
      })
      .catch((e) => {
        setErrMsg(e.message || 'This invite link is invalid or has expired.');
        setStatus('invalid');
      });
  }, [token]);

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setErrMsg('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setErrMsg('Passwords do not match.'); return; }
    setErrMsg('');
    setStatus('submitting');
    try {
      const r = await fetch(`${API}/family/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password, name: name.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Something went wrong.');
      setStatus('done');
      setTimeout(() => navigate('/'), 1800);
    } catch (e) {
      setErrMsg(e.message || 'Something went wrong. Please try again.');
      setStatus('valid');
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0f0e0d] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Crown className="h-6 w-6 text-amber-400" />
          <span className="text-lg font-bold text-neutral-100 tracking-tight">Crown &amp; Rose</span>
        </div>

        {/* Loading */}
        {status === 'loading' && (
          <div className="flex flex-col items-center gap-3 text-neutral-400">
            <Loader2 className="h-7 w-7 animate-spin" />
            <p className="text-sm">Validating your invite…</p>
          </div>
        )}

        {/* Invalid */}
        {status === 'invalid' && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/8 p-8 text-center">
            <AlertTriangle className="h-10 w-10 text-rose-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-neutral-100 mb-2">Invite link invalid</h1>
            <p className="text-sm text-neutral-400 mb-6">{errMsg || 'This invite link is invalid or has already been used.'}</p>
            <Button variant="outline" className="border-neutral-700 text-neutral-300" onClick={() => navigate('/login')}>
              Go to sign in
            </Button>
          </div>
        )}

        {/* Done */}
        {status === 'done' && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/8 p-8 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-neutral-100 mb-2">Welcome to The Loom!</h1>
            <p className="text-sm text-neutral-400">Your account is ready. Taking you to the dashboard…</p>
          </div>
        )}

        {/* Form */}
        {(status === 'valid' || status === 'submitting') && (
          <div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-8">
            <h1 className="text-2xl font-bold text-neutral-100 mb-1">You're invited!</h1>
            <p className="text-sm text-neutral-400 mb-6">
              Set up your Crown &amp; Rose account for <strong className="text-neutral-200">{invite.email}</strong>.
              You'll get full Studio-equivalent access — free.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="family-name" className="text-[12px] text-neutral-400">Your name</Label>
                <Input
                  id="family-name"
                  type="text"
                  autoComplete="name"
                  placeholder="Jane Austen"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-neutral-800 border-neutral-700 text-neutral-100 placeholder:text-neutral-600"
                  data-testid="family-accept-name"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="family-password" className="text-[12px] text-neutral-400">Choose a password</Label>
                <div className="relative">
                  <Input
                    id="family-password"
                    type={showPw ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-neutral-800 border-neutral-700 text-neutral-100 placeholder:text-neutral-600 pr-10"
                    data-testid="family-accept-password"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="family-confirm" className="text-[12px] text-neutral-400">Confirm password</Label>
                <Input
                  id="family-confirm"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Same password again"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="bg-neutral-800 border-neutral-700 text-neutral-100 placeholder:text-neutral-600"
                  data-testid="family-accept-confirm"
                />
              </div>

              {errMsg && (
                <p className="text-[12px] text-rose-400 flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  {errMsg}
                </p>
              )}

              <Button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                data-testid="family-accept-submit"
              >
                {status === 'submitting' ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating account…</>
                ) : (
                  'Create my account'
                )}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
